/* eslint-disable jsx-a11y/media-has-caption */
import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import ReactPlayer from 'react-player';
import classNames from 'classnames';
import LearningCheckQuestions from '../../../micro/learningCheck/LearningCheckQuestions';
import { purple, yellow, universalStyles } from '../../../../app/theme';
import { BASE_URL, IMAGE_PATHS, VIDEO_PATHS } from '../../../../constants/paths.constants';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  gradientBox: {
    background:
      'transparent linear-gradient(270deg, #F7B92B00 0%, #F7B92B19 34%, #F7B92B4D 58%, #F7B92B80 87%, #F7B92B98 100%) 0% 0% no-repeat padding-box',
    width: '100%',
    margin: '50px 33px 50px -50px',
    paddingLeft: 33,
    paddingRight: 33,
    '& > ul': {
      paddingTop: 25,
      paddingBottom: 25,
    },
    '& > ul > li::before': {
      color: purple,
    },
  },
  flexbox: {
    width: '100%',
    display: 'flex',
  },
  diagrambox: {
    width: '60%',
    marginRight: 21,
  },
  captionbox: {
    width: '40%',
  },
  halfbox: {
    width: '50%',
  },
  heading2: {
    fontStyle: 'italic',
    textAlign: 'center',
  },
  subtitle: {
    marginBottom: 0,
  },
  caption: {
    textAlign: 'center',
    paddingLeft: '15%',
    paddingRight: '15%',
  },
  text: {
    marginTop: 15,
  },
  asterix: {
    marginTop: 15,
  },
  note: {
    color: purple,
    marginTop: 30,
    marginBottom: 30,
    textAlign: 'center',
    padding: '10px 10% 10px 10%',
    boxSizing: 'border-box',
    border: `solid ${purple} 2px`,
  },
  moaVideo: {
    width: '80%',
    aspectRatio: '16/9',
    margin: '25px 10% 6px',
  },
  bioVideo: {
    width: '100%',
    aspectRatio: '1.77',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: 6,
    marginTop: 15,
    display: 'block',
    boxSizing: 'border-box',
    border: `solid ${yellow} 6px`,
  },
  image: {
    width: '100%',
  },
  fullImage: {
    width: '80%',
    margin: '0 10%',
  },
  section: {
    marginTop: 50,
    marginBottom: 50,
  },
  breaker: {
    marginTop: 25,
  },
  superscript: {
    fontSize: 11,
  },
}));

type AcquisitionPage03Props = {
  setNextDisabled: (disabled: boolean) => void;
};

const AcquisitionPage03 = ({ setNextDisabled }: AcquisitionPage03Props): JSX.Element => {
  const classes = useStyles();

  return (
    <>
      <div className={classes.grayBox}>
        <div className={classes.whiteBox}>
          <Typography variant="h4">Mechanism of Action</Typography>
          <div className={classes.moaVideo}>
            <ReactPlayer className={classes.responsiveVideo} url={VIDEO_PATHS.MOA.path} controls />
          </div>
          <ul className={classNames(classes.list, classes.section)}>
            <li>
              <sup className={classes.superscript}>18</sup>
              F-Fluciclovine is a synthetic amino acid PET imaging agent labeled with&nbsp;
              {/* eslint-disable-next-line prettier/prettier */}
              <sup className={classes.superscript}>18</sup>
              F
            </li>
            <ul className={classNames(classes.list, classes.nestedList)}>
              <li>Transported most like glutamine</li>
            </ul>
            <li>
              Recognized and taken up by amino acid transporters that are upregulated in many cancer cells, including
              prostate cancer
            </li>
            <li>Principal transporters involved in Axumin uptake are LAT1 and ASCT2</li>
            <ul className={classNames(classes.list, classes.nestedList)}>
              <li>
                LAT1 and ASCT2 expression levels have been correlated with a more aggressive phenotype of prostate
                cancer
              </li>
            </ul>
            <li>Axumin is not metabolized or incorporated into newly synthesized protein</li>
          </ul>
          <Typography variant="h4">Axumin Molecular Structure</Typography>
          <img
            className={classNames(classes.image, classes.fullImage)}
            src={`${BASE_URL}${IMAGE_PATHS.MOLECULAR_STRUCTURE.path}`}
            alt={`${IMAGE_PATHS.MOLECULAR_STRUCTURE.alt}`}
          />
          <Typography variant="body1" className={classes.caption}>
            Anti-1-amino-3-
            <sup className={classes.superscript}>18</sup>
            F-fluorocyclobutane-1-carboxylic acid (also known as FACBC) is an artificial amino acid PET imaging agent
            labeled with&nbsp;
            {/* eslint-disable-next-line prettier/prettier */}
            <sup className={classes.superscript}>18</sup>
            F (molecular weight 132 g/mole)
          </Typography>
          <div className={classes.gradientBox}>
            <ul className={classNames(classes.list, classes.section)}>
              <li>Axumin is the Fluorine 18 (F 18) labeled synthetic amino acid analog, fluciclovine</li>
              <li>
                <sup className={classes.superscript}>18</sup>
                F-Fluciclovine is a radioactive diagnostic labeling agent used with PET imaging
              </li>
              <li>
                This chemical structure may be referred to as FACBC,&nbsp;
                <sup className={classes.superscript}>18</sup>
                F-Fluciclovine, or Axumin
              </li>
            </ul>
          </div>
          <Typography variant="h4">Biodistribution: The Pharmacokinetics of Axumin</Typography>
          <Typography variant="body1" className={classes.text}>
            Amino acid transporters are ubiquitous throughout the body, but are upregulated in prostate cancer
          </Typography>
          <div className={classNames(classes.flexbox, classes.section)}>
            <div className={classes.halfbox}>
              <Typography variant="h5" className={classes.subtitle}>
                Distribution after IV dosing
              </Typography>
              <ul className={classNames(classes.list)}>
                <li>Red bone marrow: 12%*</li>
                <li>Over time, Axumin distributes to skeletal muscle</li>
              </ul>
              <Typography variant="h5" className={classNames(classes.subtitle, classes.breaker)}>
                Excretion after IV dosing
              </Typography>
              <ul className={classNames(classes.list)}>
                <li>3%* excreted in urine (4 hours p.i.)</li>
                <li>5%* excreted in urine (24 hours p.i.)</li>
                <li>Typically, minimal to no interference with image interpretation from bladder activity</li>
              </ul>
            </div>
            <div className={classes.halfbox}>
              <Typography variant="h5" className={classNames(classes.subtitle, classes.heading2)}>
                Distribution after IV dosing
              </Typography>
              <div className={classes.bioVideo}>
                <ReactPlayer className={classes.responsiveVideo} url={VIDEO_PATHS.BIO.path} controls />
              </div>
              <Typography variant="caption" className={classes.asterix}>
                * % of administered radioactivity
              </Typography>
            </div>
          </div>
          <Typography variant="h4" className={classes.section}>
            Biodistribution: The Pharmacokinetics of Axumin
          </Typography>
          <div className={classes.flexbox}>
            <div className={classes.diagrambox}>
              <img
                className={classes.image}
                src={`${BASE_URL}${IMAGE_PATHS.BIODISTRIBUTION_GRAPH_PART2.path}`}
                alt={`${IMAGE_PATHS.BIODISTRIBUTION_GRAPH_PART2.alt}`}
              />
            </div>
            <div className={classes.captionbox}>
              <Typography variant="body1" className={classes.note}>
                <strong>Note: </strong>
                Grey box denotes typical scan duration; mean and interpolated data from 6 subjects; study GE148-001
              </Typography>
              <ul className={classNames(classes.list)}>
                <li>
                  Tumor-to-normal tissue contrast is highest between 4 and 10 minutes p.i., with a 61% reduction in mean
                  tumor uptake at 90 minutes
                </li>
                <li>No extended uptake period</li>
                <li>Axumin administered on scanner table</li>
                <li>Begin PET scanning 3 to 5 minutes after injection</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <LearningCheckQuestions module="axuminAcquisition" page={3} setNextDisabled={setNextDisabled} trainingId={1} />
    </>
  );
};

export default AcquisitionPage03;
